// @flow
import _ from "lodash/fp"
import Passenger from "@kiwicom/frontend-app/scripts/containers/mutable/Passenger"

import type { PassengerId } from "mmb/types/primitiveTypes"
import type {
  Seatmap,
  PriceType,
  Seat,
  RowType,
  OrderedSeat,
  RequestedSeats,
  MinMaxPrices,
} from "./types"
import { PRICE_TYPES, SEAT_STATES } from "../../consts/bookingSeatingConsts"

/* ®
 * Helper function that classifies price ranges.
 * TODO - right now the function is very naive. Later on, we can implement
 * better approach. Something like jenks or natual breaks algorithms, but those
 * sure are an overkill at least for now.
 */

function getThresholds(prices: Array<number>) {
  const sorted = _.sortBy(_.identity, prices)
  const mean = _.mean(sorted)
  const lowest = _.head(sorted)
  const highest = _.last(sorted)

  return {
    low: (lowest + mean) / 2,
    high: (highest + mean) / 2,
  }
}

export function classifyPrices(prices: Array<number>): { [key: string]: PriceType } {
  if (prices.length === 0) {
    return {}
  }
  if (prices.length === 1) {
    return { [prices[0]]: "cheap" }
  }

  const thresholds = getThresholds(prices)

  return prices.reduce((acc, price) => {
    if (price > thresholds.high) {
      acc[price] = PRICE_TYPES.EXPENSIVE // eslint-disable-line no-param-reassign
    } else if (price < thresholds.low) {
      acc[price] = PRICE_TYPES.CHEAP // eslint-disable-line no-param-reassign
    } else {
      acc[price] = PRICE_TYPES.NORMAL // eslint-disable-line no-param-reassign
    }
    return acc
  }, {})
}

export const getFullPrice = (seat: Seat): number => seat.price.amount + seat.margin.amount

export function extractSeatmapPrices(seatmap: Seatmap): Array<number> {
  const extract = _.compose(
    _.uniq,
    _.map((seat: Seat) => getFullPrice(seat)),
    _.filter(({ state, type }) => type === "seat" && state === SEAT_STATES.AVAILABLE),
    _.flattenDeep,
    _.values,
  )

  return extract(seatmap)
}

export function classifySeatmapPrices(seatmap: Seatmap): { [price: string]: PriceType } {
  return classifyPrices(extractSeatmapPrices(seatmap))
}

export function getMinMaxPrices(seatmap: Seatmap): MinMaxPrices {
  const prices = classifyPrices(extractSeatmapPrices(seatmap))

  const groupedPrices = Object.keys(prices).reduce((acc, priceKey) => {
    const price = Number(priceKey)
    const priceType = prices[priceKey]
    return {
      ...acc,
      [priceType]: acc[priceType] ? [...acc[priceType], price] : [price],
    }
  }, {})

  return Object.keys(groupedPrices).reduce(
    (acc, val) => ({
      ...acc,
      [val]: {
        min: _.last(groupedPrices[val]),
        max: _.head(groupedPrices[val]),
      },
    }),
    {},
  )
}

export function findSeatById(seatmap: Seatmap, seatId: string): Seat | null {
  if (!seatId) {
    return null
  }
  const [rowId, seatColumn] = seatId.split("-")
  return (
    (seatmap[rowId] && _.flatten(seatmap[rowId]).find(seat => seat.column === seatColumn)) || null
  )
}

/*
 * See http://ramdajs.com/docs/#intersperse
 * Lodash doesn't have one.
 * @param element to put between elements
 * @param arr to intersperse
 */
export function intersperse(element: any, arr: Array<any>) {
  // eslint-disable-line flowtype/no-weak-types
  // $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
  const pairs = _.zip(_.tail(arr), _.slice(arr, 0, -1))

  const inner = _.map(pairs, _.isFunction(element) ? _.spread(element) : _.constant(element))

  return _.flow(
    _.zip(inner),
    _.flatten,
    _.tail,
  )(arr)
}

/*
 * Width of a row including spaces between seats
 * @param row
 * @returns {width}
 */
export const rowWidth = (row: RowType): number =>
  _.compose(
    _.sum,
    _.map(_.size),
  )(row) +
  (row.length - 1)

export function isExtraLegroomRow(row: RowType) {
  return _.flatten(row).every(({ features }) => features && features.includes("extra leg room"))
}

export function isExitRow(row: RowType) {
  return _.flatten(row).every(({ features }) => features && features.includes("exit row"))
}

export const getOrderedSeat = (
  // eslint-disable-next-line flowtype/no-weak-types
  additionalServices: Object,
  seatId: string,
): OrderedSeat => // eslint-disable-line flowtype/no-weak-types
  _.flow(
    _.entries,
    _.map(([passenger, additionalService]) => ({
      ...additionalService,
      passenger,
    })),
    _.find(change => _.isEqual(change.seat, seatId)),
    _.omit(["seat"]),
  )(additionalServices)

export const findPassengerById = (
  passengers: Array<Passenger>,
  passengerId: PassengerId,
  // $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
): Passenger => _.find(passenger => _.isEqual(passenger.id, passengerId), passengers)

export const getPassengersWithoutRequestedSeats = (
  passengers: Array<Passenger>,
  // eslint-disable-next-line flowtype/no-weak-types
  requestedSeats: RequestedSeats,
): Array<Passenger> => // eslint-disable-line flowtype/no-weak-types
  _.filter(passenger =>
    _.flow(
      _.keys,
      _.negate(_.find(key => _.isEqual(parseInt(key), passenger.id))),
    )(requestedSeats),
  )(passengers)
